import { ISelectOption } from '@private/components';
import * as yup from 'yup';

import { FILTRATION_TYPES } from 'constants/common';
import { FILTER_OPERATIONS_SORT_STRATEGIES } from 'store/userConfiguration';
import { INullable } from 'utils/types';

import {
  ANALYTICS_DICTIONARY_PREFIX,
  AUTH_DICTIONARY_PREFIX,
  BUILDER_DICTIONARY_PREFIX,
  DICTIONARY_PREFIX,
} from './constants';
import {
  FILTER_OPERATIONS,
  IAdditionalCondition,
  IAppliedFilter,
  IAppliedFilterValue,
  IFilterValue,
  INumberRange,
} from './types';

export const presetInitialValues = {
  name: '',
};

export const getOppositeLikeOperation = (operation?: INullable<FILTER_OPERATIONS>) =>
  operation === FILTER_OPERATIONS.LIKE
    ? FILTER_OPERATIONS.NOT_LIKE
    : FILTER_OPERATIONS.LIKE;

const getActiveLikeValues = (condition?: IAdditionalCondition): string[] => {
  if (!condition || condition.enabled === false) {
    return [];
  }

  const values = Array.isArray(condition.value) ? condition.value : [condition.value];

  return values.filter(
    (item, index): item is string =>
      typeof item === 'string' &&
      !!item.trim() &&
      !condition.disabledIndexes?.includes(index),
  );
};

export const getLikeConflictColumns = (filters: IAppliedFilter): string[] =>
  Object.entries(filters)
    .filter(([, filter]) => {
      const additionalValues = getActiveLikeValues(filter?.additionalCondition);

      return getActiveLikeValues(filter).some((item) => additionalValues.includes(item));
    })
    .map(([columnKey]) => columnKey);

export const isAppliedFilterActive = (filter: IAppliedFilterValue): boolean => {
  if (filter.enabled === false) {
    return false;
  }

  if (Array.isArray(filter.value) && filter.value.length > 0) {
    const disabledCount = filter.disabledIndexes?.length ?? 0;
    if (disabledCount >= filter.value.length) {
      return false;
    }
  }

  return true;
};

export const presetValidationSchema = yup.object().shape({
  name: yup.string().nullable().required('common.requiredField'),
});

export const isFilterNumberRange = (value: IFilterValue): value is INumberRange => {
  const isObject = value instanceof Object;

  if (isObject) {
    const hasMinMax =
      Object.hasOwnProperty.call(value, 'min') &&
      Object.hasOwnProperty.call(value, 'max');
    return hasMinMax;
  }
  return false;
};

export const isOption = (value: IFilterValue | unknown): value is ISelectOption => {
  const isObject = value instanceof Object;

  if (isObject) {
    const hasOption =
      Object.hasOwnProperty.call(value, 'label') &&
      Object.hasOwnProperty.call(value, 'value');
    return hasOption;
  }
  return false;
};

const DICTIONARY_INTL_PREFIXES: Record<string, string> = {
  [ANALYTICS_DICTIONARY_PREFIX]: 'analyticsDictionaries',
  [AUTH_DICTIONARY_PREFIX]: 'authDictionaries',
  [BUILDER_DICTIONARY_PREFIX]: 'builderDictionaries',
  [DICTIONARY_PREFIX]: 'dictionaries',
};

/*
  Payment dictionaries whose option labels come from API data (country names, payment method
  descriptions, result-code messages) rather than from translation.json. They have no intl key,
  so callers must keep whichever label the option list produced.
*/
const API_LABELLED_DICTIONARIES = [
  'countryCode',
  'currency',
  'paymentMethod',
  'resultCode',
];

interface IGetDictionaryOptionIntlKey {
  type: string;
  value: string;
}

/*
  Maps a column's backend filter type (e.g. `AnalyticsDictionary.notificationTypes`) plus a raw
  option value to the translation key that labels it, or null when that dictionary is not
  translation-driven. Shared by the filter option list and the applied-filter chips so both
  resolve the same label under the current locale.
*/
export const getDictionaryOptionIntlKey = ({
  type,
  value,
}: IGetDictionaryOptionIntlKey): null | string => {
  const [typePrefix, dictionaryName] = type.split('.');
  const intlPrefix = DICTIONARY_INTL_PREFIXES[typePrefix];

  if (
    !intlPrefix ||
    !dictionaryName ||
    API_LABELLED_DICTIONARIES.includes(dictionaryName)
  ) {
    return null;
  }

  // The payment dictionary exposes `transactionTypes`, but its labels live under the singular key.
  const intlName =
    typePrefix === DICTIONARY_PREFIX && dictionaryName === 'transactionTypes'
      ? 'transactionType'
      : dictionaryName;

  return `${intlPrefix}.${intlName}.${value}`;
};

const BASE_OPERATOR_SORT: FILTER_OPERATIONS[] = [
  FILTER_OPERATIONS.EQUALS_NULL,
  FILTER_OPERATIONS.NOT_EQUALS_NULL,
  FILTER_OPERATIONS.CONTAINS,
  FILTER_OPERATIONS.RANGE,
  FILTER_OPERATIONS.MORE_OR_EQUALS,
  FILTER_OPERATIONS.LESS_OR_EQUALS,
];

const EQUALS_FIRST: FILTER_OPERATIONS[] = [
  FILTER_OPERATIONS.EQUALS,
  FILTER_OPERATIONS.NOT_EQUALS,
  FILTER_OPERATIONS.LIKE,
  FILTER_OPERATIONS.NOT_LIKE,
];

const LIKES_FIRST: FILTER_OPERATIONS[] = [
  FILTER_OPERATIONS.LIKE,
  FILTER_OPERATIONS.NOT_LIKE,
  FILTER_OPERATIONS.EQUALS,
  FILTER_OPERATIONS.NOT_EQUALS,
];

const sortOperations =
  (
    isSelect?: boolean,
    filterOperationsSortStrategy?: FILTER_OPERATIONS_SORT_STRATEGIES,
    defaultLikesFirst?: boolean,
  ) =>
  (a: FILTER_OPERATIONS, b: FILTER_OPERATIONS): number => {
    const isEqualsFirst =
      filterOperationsSortStrategy === FILTER_OPERATIONS_SORT_STRATEGIES.EQUALS_FIRST;

    const customSortOrder = (
      isEqualsFirst || (isSelect && !defaultLikesFirst) ? EQUALS_FIRST : LIKES_FIRST
    ).concat(BASE_OPERATOR_SORT);

    const indexA = customSortOrder.indexOf(a);
    const indexB = customSortOrder.indexOf(b);

    if (indexA === -1) {
      return 1; // Move items not in the custom order to the end
    }
    if (indexB === -1) {
      return -1; // Move items not in the custom order to the end
    }

    return indexA - indexB;
  };

export const getDateOperationsOptions = (
  options: FILTER_OPERATIONS[],
  columnType: string,
) => {
  const includeRange =
    options.includes(FILTER_OPERATIONS.LESS_OR_EQUALS) &&
    options.includes(FILTER_OPERATIONS.MORE_OR_EQUALS) &&
    !options.includes(FILTER_OPERATIONS.RANGE);

  let strategyOptionsList: FILTER_OPERATIONS[] = [];

  if (includeRange) {
    strategyOptionsList.push(FILTER_OPERATIONS.RANGE);
  }

  strategyOptionsList = [
    ...strategyOptionsList,
    ...options.filter(
      (item) =>
        columnType === FILTRATION_TYPES.LOCAL_DATE ||
        (columnType === FILTRATION_TYPES.ZONED_DATE_TIME &&
          !(item === FILTER_OPERATIONS.EQUALS || item === FILTER_OPERATIONS.NOT_EQUALS)),
    ),
  ];

  return strategyOptionsList.sort(sortOperations());
};

interface IGetStrategies {
  betweenList?: Set<string>;
  columnKey: string;
  defaultLikesFirstFields?: string[];
  filterOperationsSortStrategy?: FILTER_OPERATIONS_SORT_STRATEGIES;
  isSelect?: boolean;
  listOperations: FILTER_OPERATIONS[];
  searchNullable: boolean;
  type: FILTRATION_TYPES;
}

export const getStrategies = ({
  betweenList,
  columnKey,
  defaultLikesFirstFields,
  filterOperationsSortStrategy,
  isSelect,
  listOperations,
  searchNullable,
  type,
}: IGetStrategies) => {
  if (type === FILTRATION_TYPES.ZONED_DATE_TIME || type === FILTRATION_TYPES.LOCAL_DATE) {
    return getDateOperationsOptions(listOperations, type);
  }

  const operationsList = [...listOperations];

  const needIncludeRange = columnKey && betweenList?.has(columnKey);

  if (needIncludeRange) {
    const withoutRange =
      listOperations.includes(FILTER_OPERATIONS.LESS_OR_EQUALS) &&
      listOperations.includes(FILTER_OPERATIONS.MORE_OR_EQUALS) &&
      !listOperations.includes(FILTER_OPERATIONS.RANGE);

    if (withoutRange) {
      operationsList.push(FILTER_OPERATIONS.RANGE);
    }
  }

  if (searchNullable) {
    operationsList.push(FILTER_OPERATIONS.NOT_EQUALS_NULL);
    operationsList.push(FILTER_OPERATIONS.EQUALS_NULL);
  }

  const defaultLikesFirst = defaultLikesFirstFields?.some((item) => item === columnKey);

  return operationsList.sort(
    sortOperations(isSelect, filterOperationsSortStrategy, defaultLikesFirst),
  );
};
